import { useEffect, useMemo, useRef, useState } from 'react';
import { Landmark } from 'lucide-react';
import { Card } from '@components/base/layout';
import { Text, Badge, Icon } from '@components/base/display';
import { Alert, Spinner } from '@components/base/feedback';
import { DataList, useDataList } from '@components/integrated/data-list';
import type { DataListConfig } from '@components/integrated/data-list';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromDataList,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { comptabiliteService, type ComptabiliteRow } from '../api/comptabiliteService';
import { useClaimDataStore } from './claimDataStore';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ComptabiliteCardProps {
  /** Internal claim id — the card (re)fetches the claim's movements from it. */
  idSin: string;
  /**
   * Veos session id. Not needed by the read-only fetch (the ERP proxy carries
   * the session); accepted for parity with the other self-fetching claim cards
   * and the registry's prop contract.
   */
  ses?: string;
  /**
   * Per-tenant layout overrides injected by the `withComponentRules` HOC.
   * Underscored to match the injected meta-prop convention; `undefined` at
   * baseline ⇒ the config is returned unchanged (byte-identical render).
   * Rule authors target the **stable column keys** — visible `date` / `sens` /
   * `categ` / `beneficiaire` / `libelle` / `montant` / `util`, hidden by
   * default `idSrg` / `idrBor` / `idBor` / `dateLchq` / `datgenLchq` /
   * `datoOper` / `datoOper2` (revealed with `showColumn`) — to rename / hide /
   * show / reorder / tooltip a column, and `title` to rename the card.
   */
  _layoutOverrides?: LayoutOverrides;
}

/** Stable registry / layout-config id of this card. */
const COMPONENT_ID = 'claimflow.comptabiliteCard';

/** Card-level texts (title / status / empty states), kept out of the JSX. */
const COMPTABILITE_CARD_TEXT = {
  title: 'Comptabilité',
  loadError: 'Erreur lors du chargement de la comptabilité.',
  empty: 'Aucun mouvement',
} as const;

// ---------------------------------------------------------------------------
// Config
//
// Declarative DataListConfig with stable column keys; French labels live only
// in `column.label` (display-only / overridable). Each key reads its raw
// `WS2_GS_TAB_COMPTABILITE` field through `dataSource.fieldMapping`, so the
// native `date` / `currency` cell renderers format the raw values (epoch ms →
// `dd/MM/yy`, number → `82,80 €`) and the client-side sort compares the raw
// values too: epoch numbers sort chronologically, amounts numerically. The list
// keeps the API order until the user clicks a header (none → asc → desc).
// ---------------------------------------------------------------------------

/**
 * Header style parity with the "Synthèse des coûts" card right above: the base
 * Table header's uppercase/tracked default is overridden with a normal-case,
 * secondary tone.
 */
const HEADER_CLASSNAME =
  '!normal-case !text-xs !font-medium !tracking-normal text-secondary-500';

/** Display format of every date column (VEOS dates are epoch ms). */
const DATE_FORMAT = 'dd/MM/yy';

/** A row as handed to the DataList (it keys rows by `row.id`). */
type ComptabiliteListRow = ComptabiliteRow & { id: number };

function createComptabiliteListConfig(rows: ComptabiliteListRow[]): DataListConfig {
  return {
    id: COMPONENT_ID,

    dataSource: {
      data: rows as unknown as Record<string, unknown>[],
      // Column key → raw VEOS field (what the cell displays AND sorts on).
      fieldMapping: {
        date: 'DTCRE_SRG',
        sens: 'ORIGINE',
        categ: 'CD_CATEG',
        beneficiaire: 'NOM_PER',
        libelle: 'LIB_SRG',
        montant: 'MNT_SRG',
        util: 'CD_UTIL',
        idSrg: 'ID_SRG',
        idrBor: 'IDR_BOR',
        idBor: 'ID_BOR',
        dateLchq: 'DATE_LCHQ',
        datgenLchq: 'DATGEN_LCHQ',
        datoOper: 'DATO_OPER',
        datoOper2: 'DATO_OPER2',
      },
    },

    columns: {
      // --- Visible by default (mockup order) -------------------------------
      date: {
        label: 'Date',
        headerTooltip: 'Date de création du mouvement',
        order: 1,
        align: 'left',
        type: 'date',
        typeOptions: { format: DATE_FORMAT },
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm',
      },
      sens: {
        label: 'Sens',
        headerTooltip: 'Origine du mouvement',
        order: 2,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm',
      },
      categ: {
        label: 'Categ',
        headerTooltip: 'Catégorie du bénéficiaire',
        order: 3,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm',
      },
      beneficiaire: {
        label: 'Bénéficiaire',
        order: 4,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm',
      },
      libelle: {
        label: 'Libellé',
        order: 5,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm',
      },
      montant: {
        label: 'Montant',
        order: 6,
        align: 'right',
        type: 'currency',
        typeOptions: { currency: 'EUR', locale: 'fr-FR' },
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm',
      },
      util: {
        label: 'Util',
        headerTooltip: 'Utilisateur ayant saisi le mouvement',
        order: 7,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm',
      },

      // --- Hidden by default (an admin can reveal them with showColumn) ----
      idSrg: {
        label: 'N° mouvement',
        order: 8,
        hidden: true,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm',
      },
      idrBor: {
        label: 'Réf. bordereau',
        order: 9,
        hidden: true,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm',
      },
      idBor: {
        label: 'N° bordereau',
        order: 10,
        hidden: true,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm',
      },
      dateLchq: {
        label: 'Date lettre chèque',
        order: 11,
        hidden: true,
        align: 'left',
        type: 'date',
        typeOptions: { format: DATE_FORMAT },
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm',
      },
      datgenLchq: {
        label: 'Génération lettre chèque',
        order: 12,
        hidden: true,
        align: 'left',
        type: 'date',
        typeOptions: { format: DATE_FORMAT },
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm',
      },
      datoOper: {
        label: 'Date opération',
        order: 13,
        hidden: true,
        align: 'left',
        type: 'date',
        typeOptions: { format: DATE_FORMAT },
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm',
      },
      datoOper2: {
        label: 'Date opération 2',
        order: 14,
        hidden: true,
        align: 'left',
        type: 'date',
        typeOptions: { format: DATE_FORMAT },
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm',
      },
    },

    display: {
      size: 'compact',
      hoverable: false,
    },

    // Front-only sorting on the loaded rows (no API call); API order until the
    // user clicks a header.
    sorting: {
      enabled: true,
    },

    toolbar: {
      visible: false,
    },

    // Every movement on one page — no pager.
    pagination: {
      mode: 'scroll',
      pageSize: 999_999,
    },
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Identity of one fetch: the claim and the comptabilité version. */
function buildRequestKey(idSin: string, version: number): string {
  return JSON.stringify([idSin, version]);
}

/** The movements on screen, tagged with the request they answer. */
interface ComptabiliteLoadState {
  /** {@link buildRequestKey} of the request. */
  key: string;
  idSin: string;
  rows: ComptabiliteRow[];
  error: string | null;
}

export function ComptabiliteCard({ idSin, _layoutOverrides }: Readonly<ComptabiliteCardProps>) {
  // Bumped whenever a movement is recorded on the claim (Règlement, Recours) —
  // refetch on it (22853 follow-up).
  const comptabiliteVersion = useClaimDataStore((s) => s.comptabiliteVersion);

  const [loaded, setLoaded] = useState<ComptabiliteLoadState | null>(null);

  // Stale-response guard: every request takes a new sequence number and only
  // the latest one issued may write state, so a slow response (a claim the
  // user already left, a refresh overtaken by a newer one) never overwrites a
  // newer result.
  const requestRef = useRef(0);

  useEffect(() => {
    // Bumped even without a claim id, so an in-flight response is dropped.
    const requestId = ++requestRef.current;
    if (idSin === '') return;
    const key = buildRequestKey(idSin, comptabiliteVersion);
    comptabiliteService.fetchComptabilite(idSin).then(
      (rows) => {
        if (requestId !== requestRef.current) return;
        setLoaded({ key, idSin, rows, error: null });
      },
      () => {
        if (requestId !== requestRef.current) return;
        setLoaded({ key, idSin, rows: [], error: COMPTABILITE_CARD_TEXT.loadError });
      },
    );
  }, [idSin, comptabiliteVersion]);

  // Only the CURRENT claim's response is ever shown: full spinner until its
  // first response settles (first load and after a claim switch). A refetch of
  // the same claim (a règlement / recours was recorded) keeps the movements on
  // screen with a discreet header spinner and `aria-busy`. Without a claim id
  // nothing is fetched and the card reads as empty.
  const hasClaim = idSin !== '';
  const current = hasClaim && loaded?.idSin === idSin ? loaded : null;
  const isLoading = hasClaim && current === null;
  const isRefetching =
    current !== null && current.key !== buildRequestKey(idSin, comptabiliteVersion);
  const movements = useMemo(() => current?.rows ?? [], [current]);
  const loadError = current?.error ?? null;

  const rows = useMemo<ComptabiliteListRow[]>(
    () => movements.map((row) => ({ ...row, id: row.ID_SRG })),
    [movements],
  );

  const baseConfig = useMemo(() => createComptabiliteListConfig(rows), [rows]);

  usePublishLayoutConfig(
    COMPONENT_ID,
    buildPublishedConfigFromDataList(baseConfig, COMPTABILITE_CARD_TEXT.title),
  );

  const config = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  const list = useDataList(config);

  return (
    <Card padding="none">
      <Card.Header>
        <div className="flex items-center gap-2">
          <Icon icon={Landmark} size="md" color="primary" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle(COMPTABILITE_CARD_TEXT.title, _layoutOverrides)}
          </Text>
          {!isLoading && !loadError && (
            <Badge variant="default" size="sm" pill>
              {movements.length}
            </Badge>
          )}
          {/* A refetch keeps the movements on screen, with this discreet hint. */}
          {isRefetching && <Spinner size="xs" />}
        </div>
      </Card.Header>
      <Card.Content data-testid="comptabilite-content" aria-busy={isLoading || isRefetching}>
        {isLoading && (
          <div className="flex items-center justify-center min-h-24 p-5">
            <Spinner size="md" />
          </div>
        )}

        {!isLoading && loadError && (
          <div className="p-5">
            <Alert variant="error">{loadError}</Alert>
          </div>
        )}

        {!isLoading && !loadError && movements.length === 0 && (
          <div className="p-5">
            <Text size="sm" color="muted">
              {COMPTABILITE_CARD_TEXT.empty}
            </Text>
          </div>
        )}

        {!isLoading && !loadError && movements.length > 0 && (
          <DataList config={config} list={list}>
            <DataList.Table />
          </DataList>
        )}
      </Card.Content>
    </Card>
  );
}
